import { useEffect, useState } from "react";
import { Download, MonitorDown, Smartphone } from "lucide-react";
import PageLayout from "../components/PageLayout";
import {
  App,
  type AppLatestResponse,
  type DesktopLatestResponse,
} from "../lib/api";

/**
 * Download page for the two native clients.
 *
 * Each card loads independently and fails independently: a platform with no
 * published build shows "not published yet" while the other still offers its
 * download. Previously a single 404 blanked the whole page.
 */

function formatBytes(n: number): string {
  if (!n) return "—";
  const mb = n / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

type Load<T> = { state: "loading" } | { state: "none" } | { state: "error"; msg: string } | { state: "ok"; data: T };

function useRelease<T>(fetcher: () => Promise<T>): Load<T> {
  const [v, setV] = useState<Load<T>>({ state: "loading" });
  useEffect(() => {
    let alive = true;
    fetcher()
      .then((d) => alive && setV({ state: "ok", data: d }))
      .catch((e) => {
        if (!alive) return;
        if (e?.response?.status === 404) setV({ state: "none" });
        else setV({ state: "error", msg: e?.response?.data?.detail || e?.message || "Failed to load" });
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return v;
}

function Card({
  icon, title, subtitle, children,
}: {
  icon: React.ReactNode; title: string; subtitle: string; children: React.ReactNode;
}) {
  return (
    <div className="bg-surface rounded-2xl border border-line p-5 flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-11 h-11 rounded-2xl bg-accent-gold/15 text-accent-gold flex items-center justify-center shrink-0">
          {icon}
        </span>
        <div className="min-w-0">
          <p className="font-display text-[20px] tracking-[-0.3px] text-text-primary leading-tight">
            {title}
          </p>
          <p className="text-[11px] font-data uppercase tracking-[0.06em] text-text-muted mt-0.5">
            {subtitle}
          </p>
        </div>
      </div>
      {children}
    </div>
  );
}

function Meta({ version, size, date, sha, notes }: {
  version: string; size: number; date: string; sha: string; notes?: string;
}) {
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
        <span className="text-[12px] font-data uppercase tracking-[0.04em] text-accent-gold">
          v{version}
        </span>
        <span className="text-[11px] font-data uppercase tracking-[0.04em] text-text-secondary">
          {formatBytes(size)} · {formatDate(date)}
        </span>
      </div>
      {notes && (
        <p className="text-[13px] font-ui text-text-secondary leading-relaxed mb-4 whitespace-pre-line">
          {notes}
        </p>
      )}
      <p className="text-[11px] font-data uppercase tracking-[0.04em] text-text-muted mt-4 break-all">
        sha256 · {sha.slice(0, 16)}…{sha.slice(-16)}
      </p>
    </>
  );
}

const btn =
  "inline-flex items-center gap-2 px-5 py-3 rounded-pill bg-accent-gold text-canvas font-ui font-semibold text-[13px] uppercase tracking-[0.04em] hover:brightness-110 transition-fast self-start";

function Pending({ label }: { label: string }) {
  return <p className="text-[13px] font-ui text-text-muted">{label}</p>;
}

export default function DownloadPage() {
  const android = useRelease<AppLatestResponse>(() => App.latest());
  const windows = useRelease<DesktopLatestResponse>(() => App.desktopLatest());

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-6 max-w-4xl">
        <h1 className="font-display text-[36px] sm:text-[44px] tracking-[-0.5px] text-text-primary leading-none mb-2">
          Get the apps
        </h1>
        <p className="text-[13px] font-ui text-text-secondary mb-7">
          Native clients play more formats directly, so less work happens on the server.
        </p>

        <div className="grid gap-5 md:grid-cols-2">
          <Card
            icon={<Smartphone size={20} />}
            title="Android app"
            subtitle="Android TV · Google TV · phone"
          >
            {android.state === "loading" && <Pending label="Checking for a build…" />}
            {android.state === "none" && <Pending label="No build published yet." />}
            {android.state === "error" && <Pending label={android.msg} />}
            {android.state === "ok" && (
              <>
                <a
                  href={android.data.download_url}
                  download={`brainzr-tv-${android.data.version_name}.apk`}
                  className={btn}
                >
                  <Download size={16} />
                  Download APK
                </a>
                <Meta
                  version={android.data.version_name}
                  size={android.data.size_bytes}
                  date={android.data.uploaded_at}
                  sha={android.data.sha256}
                  notes={android.data.notes}
                />
                <div className="mt-5 text-[12px] font-ui text-text-secondary leading-relaxed">
                  <p className="font-medium text-text-primary mb-1">On a TV</p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>Download it on the TV, or copy it across by USB.</li>
                    <li>
                      Settings → Apps → Special app access → Install unknown apps,
                      and allow the app you are installing from.
                    </li>
                    <li>Open the file and follow the prompt.</li>
                  </ol>
                </div>
              </>
            )}
          </Card>

          <Card
            icon={<MonitorDown size={20} />}
            title="Windows app"
            subtitle="Windows 10 / 11 · 64-bit"
          >
            {windows.state === "loading" && <Pending label="Checking for a build…" />}
            {windows.state === "none" && <Pending label="No build published yet." />}
            {windows.state === "error" && <Pending label={windows.msg} />}
            {windows.state === "ok" && (
              <>
                <a
                  href={windows.data.download_url}
                  download={`8K-Brainzr-Setup-${windows.data.version}.exe`}
                  className={btn}
                >
                  <Download size={16} />
                  Download installer
                </a>
                <Meta
                  version={windows.data.version}
                  size={windows.data.size_bytes}
                  date={windows.data.uploaded_at}
                  sha={windows.data.sha256}
                  notes={windows.data.notes}
                />
                <div className="mt-5 text-[12px] font-ui text-text-secondary leading-relaxed">
                  <p className="font-medium text-text-primary mb-1">First run</p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>The build is unsigned, so SmartScreen will warn: More info → Run anyway.</li>
                    <li>Allow it through the firewall on <strong>private</strong> networks, or Chromecast discovery will find nothing.</li>
                    <li>Plays MKV and HEVC natively, and casts to Chromecast.</li>
                  </ol>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </PageLayout>
  );
}
